import React, {useCallback, useMemo} from 'react';
import {useTheme} from '@mui/material';
import {assert} from 'assert-ts';
import uniqueId from 'lodash/uniqueId';
import type {OptionsObject, SnackbarKey} from 'notistack';
import {useSnackbar} from 'notistack';
import type {HttpError} from 'api/http/types';
import {px2rem} from 'theme';
import type {SnackBodyWithMessageAndNavLinkProps} from '../types';
import {IconButton} from '../../button';
import {
  SnackBodyWithErrorDetails,
  SnackBodyWithMessageAndNavLink,
} from '../components';

export type SnackVariant = 'info' | 'success' | 'warning' | 'error';

const baseSnackStyle: React.CSSProperties = {
  borderRadius: '999px',
  boxShadow: 'none',
  borderWidth: '1px',
  borderStyle: 'solid',
  paddingTop: px2rem(3),
  paddingBottom: px2rem(3),
};

type SnackOptionsMap = {[variant in SnackVariant]: OptionsObject};

type SnackMessage =
  | string
  | Omit<SnackBodyWithMessageAndNavLinkProps, 'closeSnack'>;

const isSimpleMessageType = (message: SnackMessage): message is string =>
  typeof message === 'string';

export const useSnacks = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {colors} = useTheme();

  const snackOptionsMap = useMemo<SnackOptionsMap>(
    () => ({
      info: {
        variant: 'info',
        persist: false,
        style: {
          ...baseSnackStyle,
          color: colors.burgundy,
          backgroundColor: colors.info20,
          borderColor: colors.info,
        },
      },
      success: {
        variant: 'success',
        persist: false,
        autoHideDuration: 2000,
        style: {
          ...baseSnackStyle,
          color: colors.burgundy,
          backgroundColor: colors.green,
          borderColor: colors.success,
        },
      },
      warning: {
        variant: 'warning',
        persist: false,
        style: {
          ...baseSnackStyle,
          color: colors.burgundy,
          backgroundColor: colors.warning20,
          borderColor: colors.warning,
        },
      },
      error: {
        variant: 'error',
        persist: true,
        style: {
          ...baseSnackStyle,
          color: colors.burgundy,
          backgroundColor: colors.error20,
          borderColor: colors.error,
        },
      },
    }),
    [colors],
  );

  const closeButton = useCallback(
    (key: SnackbarKey) => (
      <IconButton
        size="small"
        icon={'CloseSmall'}
        onClick={async () => closeSnackbar(key)}
      />
    ),
    [closeSnackbar],
  );

  const showSnack = useCallback(
    (variant: SnackVariant, content: SnackMessage) => {
      const key = uniqueId();
      const {message, options} = isSimpleMessageType(content)
        ? // simple string message
          {
            message: content,
            options: snackOptionsMap[variant],
          }
        : // content with message and link
          {
            message: (
              <SnackBodyWithMessageAndNavLink
                {...content}
                closeSnack={() => closeSnackbar(key)}
              />
            ),
            options: {
              ...snackOptionsMap[variant],
              // override persist to allow user to click on the link
              persist: true,
            },
          };
      enqueueSnackbar(message, {
        key,
        ...options,
        action: (key: SnackbarKey) => closeButton(key),
      });
    },
    [closeButton, closeSnackbar, enqueueSnackbar, snackOptionsMap],
  );

  const showSnackWithError = useCallback(
    (content: SnackMessage, error: HttpError) => {
      const key = uniqueId();
      assert(isSimpleMessageType(content), 'content must be a simple message');
      const snackBody = (
        <SnackBodyWithErrorDetails
          message={content}
          error={error}
          closeSnack={() => closeSnackbar(key)}
        />
      );
      const options = {...snackOptionsMap['error'], persist: true};
      enqueueSnackbar(snackBody, {
        key,
        ...options,
        action: (key: SnackbarKey) => closeButton(key),
      });
    },
    [closeButton, closeSnackbar, enqueueSnackbar, snackOptionsMap],
  );

  return useMemo(
    () => ({
      infoSnack: (message: SnackMessage) => showSnack('info', message),
      successSnack: (message: SnackMessage) => showSnack('success', message),
      warningSnack: (message: SnackMessage) => showSnack('warning', message),
      errorSnack: (message: SnackMessage, error?: HttpError) =>
        error
          ? showSnackWithError(message, error)
          : showSnack('error', message),
      snack: (message: SnackMessage, variant: SnackVariant) =>
        showSnack(variant, message),
    }),
    [showSnack, showSnackWithError],
  );
};
